import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { Table, Button, Card, Tag, Input, Select as NHSSelect, SummaryList, Textarea, ErrorSummary, BackLink } from 'nhsuk-react-components';
import { 
    listPendingImplants, 
    approvePendingImplant, 
    rejectPendingImplant, 
    deletePendingImplant 
} from '../../actions/pendingImplantActions';
import Pagination from '../../components/pagination/Pagination.component';
import AppSpinner from '../../components/spinner/AppSpinner.component';
import Modal from '../../components/modal/Modal.component';
import Callout from '../../components/callout/Callout.component';
import PageWrapper from '../../components/layout/PageWrapper.component';

export default function PendingImplantReviewScreen() {
    const dispatch = useDispatch();
    const history = useHistory();

    const [filter, setFilter] = useState({ query: '', page: 1, filter: 'barcode', reviewStatus: 'Pending' });
    const [selectedPendingImplant, setSelectedPendingImplant] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    const pendingImplantList = useSelector((state) => state.pendingImplantList) || {};
    const { loading, error, pendingImplants = [], currentPage, totalPages, totalCount } = pendingImplantList;

    const pendingImplantApprove = useSelector((state) => state.pendingImplantApprove) || {};
    const { loading: approving, success: approveSuccess, error: approveError } = pendingImplantApprove;

    const pendingImplantReject = useSelector((state) => state.pendingImplantReject) || {};
    const { loading: rejecting, success: rejectSuccess, error: rejectError } = pendingImplantReject;

    const pendingImplantDelete = useSelector((state) => state.pendingImplantDelete) || {};
    const { success: deleteSuccess } = pendingImplantDelete;

    useEffect(() => {
        dispatch(listPendingImplants(filter));
    }, [dispatch, filter, approveSuccess, rejectSuccess, deleteSuccess]);

    const handlePageChange = (newPage) => {
        setFilter({ ...filter, page: newPage });
    };

    const handleSearchChange = (e) => {
        setFilter({ ...filter, query: e.target.value, page: 1 });
    };

    const handleFilterChange = (e) => {
        setFilter({ ...filter, filter: e.target.value, page: 1 });
    };

    const handleReviewStatusChange = (e) => {
        setFilter({ ...filter, reviewStatus: e.target.value, page: 1 });
    };

    const handleViewDetails = (pendingImplant) => {
        setSelectedPendingImplant(pendingImplant);
        setDetailsModalOpen(true);
    };

    const handleApprove = (id) => {
        const pendingImplant = pendingImplants.find(p => p.id === id);
        const isReapproval = pendingImplant?.reviewStatus === 'Approved';
        const message = isReapproval 
            ? 'Are you sure you want to re-approve this implant? It will check for duplicates before adding to the main implant database.'
            : 'Are you sure you want to approve this implant? It will be added to the main implant database.';
        
        if (window.confirm(message)) {
            dispatch(approvePendingImplant(id));
        }
    };

    const handleRejectClick = (pendingImplant) => {
        setSelectedPendingImplant(pendingImplant);
        setRejectModalOpen(true);
    };

    const handleRejectSubmit = () => {
        if (!rejectionReason.trim()) {
            alert('Please provide a rejection reason');
            return;
        }
        dispatch(rejectPendingImplant(selectedPendingImplant.id, rejectionReason));
        setRejectModalOpen(false);
        setRejectionReason('');
        setSelectedPendingImplant(null);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this pending implant?')) {
            dispatch(deletePendingImplant(id));
        }
    };

    const getStatusTag = (status) => {
        switch (status) {
            case 'Pending':
                return <Tag color="blue">Pending</Tag>;
            case 'Approved':
                return <Tag color="green">Approved</Tag>;
            case 'Rejected':
                return <Tag color="red">Rejected</Tag>;
            default:
                return <Tag>{status}</Tag>;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Container fluid>
            <Row>
                <Col>
                    <BackLink
                        asElement="a"
                        href="#"
                        onClick={() => history.goBack()}
                    >
                        Return back
                    </BackLink>
                    <PageWrapper title="Pending Implant Review" totalCount={totalCount} loading={loading} />
                    <p>Review and approve implants submitted by staff</p>

                    {approveSuccess && (
                        <Callout type="success">
                            <p>Implant approved successfully and added to the database!</p>
                        </Callout>
                    )}

                    {rejectSuccess && (
                        <Callout type="info">
                            <p>Pending implant has been rejected.</p>
                        </Callout>
                    )}

                    {(approveError || rejectError || error) && (
                        <Callout type="error">
                            <p>{approveError || rejectError || error}</p>
                        </Callout>
                    )}

                    <Card>
                        <Card.Content>
                            {/* Quick Filter Buttons */}
                            <div style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: '0.5rem', 
                                marginBottom: '1rem',
                                overflowX: 'auto',
                                padding: '0.5rem 0'
                            }}>
                                <Button
                                    onClick={() => setFilter({ ...filter, reviewStatus: '', page: 1 })}
                                    secondary={filter.reviewStatus !== ''}
                                    style={{ 
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.875rem',
                                        padding: '0.5rem 1rem'
                                    }}
                                >
                                    All
                                </Button>
                                <Button
                                    onClick={() => setFilter({ ...filter, reviewStatus: 'Pending', page: 1 })}
                                    secondary={filter.reviewStatus !== 'Pending'}
                                    style={{ 
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.875rem',
                                        padding: '0.5rem 1rem'
                                    }}
                                >
                                    Pending
                                </Button>
                                <Button
                                    onClick={() => setFilter({ ...filter, reviewStatus: 'Approved', page: 1 })}
                                    secondary={filter.reviewStatus !== 'Approved'}
                                    style={{ 
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.875rem',
                                        padding: '0.5rem 1rem'
                                    }}
                                >
                                    Approved
                                </Button>
                                <Button
                                    onClick={() => setFilter({ ...filter, reviewStatus: 'Rejected', page: 1 })}
                                    secondary={filter.reviewStatus !== 'Rejected'}
                                    style={{ 
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.875rem',
                                        padding: '0.5rem 1rem'
                                    }}
                                >
                                    Rejected
                                </Button>
                            </div>

                            <Row>
                                <Col md={3}>
                                    <NHSSelect
                                        label="Search By"
                                        value={filter.filter}
                                        onChange={handleFilterChange}
                                    >
                                        <NHSSelect.Option value="barcode">Barcode</NHSSelect.Option>
                                        <NHSSelect.Option value="manufacturer">Manufacturer</NHSSelect.Option>
                                        <NHSSelect.Option value="modelName">Model Name</NHSSelect.Option>
                                        <NHSSelect.Option value="organisationName">Organisation</NHSSelect.Option>
                                        <NHSSelect.Option value="createdByUserName">Created By</NHSSelect.Option>
                                    </NHSSelect>
                                </Col>
                                <Col md={9}>
                                    <Input
                                        label="Search"
                                        value={filter.query}
                                        onChange={handleSearchChange}
                                        placeholder="Type to search..."
                                    />
                                </Col>
                            </Row>

                            {loading ? (
                                <AppSpinner />
                            ) : (
                                <>
                                    <p style={{ marginTop: '1rem' }}>
                                        Showing <strong>{pendingImplants.length}</strong> of <strong>{totalCount || 0}</strong> pending implants
                                    </p>

                                    {pendingImplants.length === 0 ? (
                                        <p>No pending implants found.</p>
                                    ) : (
                                        <Table responsive className="compact-admin-table">
                                            <Table.Head>
                                                <Table.Row>
                                                    <Table.Cell>Status</Table.Cell>
                                                    <Table.Cell>Barcode</Table.Cell>
                                                    <Table.Cell>Manufacturer</Table.Cell>
                                                    <Table.Cell>Model</Table.Cell>
                                                    <Table.Cell>Organisation</Table.Cell>
                                                    <Table.Cell>Created By</Table.Cell>
                                                    <Table.Cell>Created At</Table.Cell>
                                                    <Table.Cell>Actions</Table.Cell>
                                                </Table.Row>
                                            </Table.Head>
                                            <Table.Body>
                                                {pendingImplants.map((pendingImplant) => (
                                                    <Table.Row key={pendingImplant.id}>
                                                        <Table.Cell>{getStatusTag(pendingImplant.reviewStatus)}</Table.Cell>
                                                        <Table.Cell>{pendingImplant.barcode || '-'}</Table.Cell>
                                                        <Table.Cell>{pendingImplant.manufacturer}</Table.Cell>
                                                        <Table.Cell>{pendingImplant.modelName}</Table.Cell>
                                                        <Table.Cell>{pendingImplant.organisationName}</Table.Cell>
                                                        <Table.Cell>{pendingImplant.createdByUserName}</Table.Cell>
                                                        <Table.Cell>{formatDate(pendingImplant.createdAt)}</Table.Cell>
                                                        <Table.Cell>
                                                            <div style={{ 
                                                                display: 'flex', 
                                                                flexWrap: 'wrap', 
                                                                gap: '0.5rem',
                                                                minWidth: '200px'
                                                            }}>
                                                                <Button
                                                                    onClick={() => handleViewDetails(pendingImplant)}
                                                                    style={{ 
                                                                        fontSize: '0.875rem', 
                                                                        padding: '0.5rem 0.75rem',
                                                                        whiteSpace: 'nowrap',
                                                                        flex: '0 0 auto'
                                                                    }}
                                                                >
                                                                    View
                                                                </Button>
                                                                {pendingImplant.reviewStatus === 'Pending' && (
                                                                    <>
                                                                        <Button
                                                                            onClick={() => handleApprove(pendingImplant.id)}
                                                                            disabled={approving}
                                                                            style={{ 
                                                                                fontSize: '0.875rem', 
                                                                                padding: '0.5rem 0.75rem',
                                                                                whiteSpace: 'nowrap',
                                                                                flex: '0 0 auto'
                                                                            }}
                                                                        >
                                                                            {approving ? 'Approving...' : 'Approve'}
                                                                        </Button>
                                                                        <Button
                                                                            secondary
                                                                            onClick={() => handleRejectClick(pendingImplant)}
                                                                            disabled={rejecting}
                                                                            style={{ 
                                                                                fontSize: '0.875rem', 
                                                                                padding: '0.5rem 0.75rem',
                                                                                whiteSpace: 'nowrap',
                                                                                flex: '0 0 auto'
                                                                            }}
                                                                        >
                                                                            Reject
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                {(pendingImplant.reviewStatus === 'Approved' || pendingImplant.reviewStatus === 'Rejected') && (
                                                                    <>
                                                                        <Button
                                                                            onClick={() => handleApprove(pendingImplant.id)}
                                                                            disabled={approving}
                                                                            style={{ 
                                                                                fontSize: '0.875rem', 
                                                                                padding: '0.5rem 0.75rem',
                                                                                whiteSpace: 'nowrap',
                                                                                flex: '0 0 auto'
                                                                            }}
                                                                        >
                                                                            {approving ? 'Re-approving...' : 'Re-approve'}
                                                                        </Button>
                                                                        <Button
                                                                            secondary
                                                                            onClick={() => handleRejectClick(pendingImplant)}
                                                                            disabled={rejecting}
                                                                            style={{ 
                                                                                fontSize: '0.875rem', 
                                                                                padding: '0.5rem 0.75rem',
                                                                                whiteSpace: 'nowrap',
                                                                                flex: '0 0 auto'
                                                                            }}
                                                                        >
                                                                            {rejecting ? 'Rejecting...' : 'Reject'}
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                <Button
                                                                    className="nhsuk-button--small btn-delete"
                                                                    onClick={() => handleDelete(pendingImplant.id)}
                                                                    style={{
                                                                        whiteSpace: 'nowrap',
                                                                        flex: '0 0 auto'
                                                                    }}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </div>
                                                        </Table.Cell>
                                                    </Table.Row>
                                                ))}
                                            </Table.Body>
                                        </Table>
                                    )}

                                    {totalPages > 1 && (
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            handlePageChange={handlePageChange}
                                        />
                                    )}
                                </>
                            )}
                        </Card.Content>
                    </Card>
                </Col>
            </Row>

            {/* Details Modal */}
            <Modal show={detailsModalOpen} handleClose={() => setDetailsModalOpen(false)}>
                {selectedPendingImplant && (
                    <>
                        <h2>Pending Implant Details</h2>
                        <SummaryList>
                            <SummaryList.Row>
                                <SummaryList.Key>Status</SummaryList.Key>
                                <SummaryList.Value>{getStatusTag(selectedPendingImplant.reviewStatus)}</SummaryList.Value>
                            </SummaryList.Row>
                            <SummaryList.Row>
                                <SummaryList.Key>Barcode/GTIN</SummaryList.Key>
                                <SummaryList.Value>{selectedPendingImplant.barcode || '-'}</SummaryList.Value>
                            </SummaryList.Row>
                            <SummaryList.Row>
                                <SummaryList.Key>Manufacturer</SummaryList.Key>
                                <SummaryList.Value>{selectedPendingImplant.manufacturer}</SummaryList.Value>
                            </SummaryList.Row>
                            <SummaryList.Row>
                                <SummaryList.Key>Model Name</SummaryList.Key>
                                <SummaryList.Value>{selectedPendingImplant.modelName}</SummaryList.Value>
                            </SummaryList.Row>
                            <SummaryList.Row>
                                <SummaryList.Key>Model Number</SummaryList.Key>
                                <SummaryList.Value>{selectedPendingImplant.modelNumber || '-'}</SummaryList.Value>
                            </SummaryList.Row>
                            <SummaryList.Row>
                                <SummaryList.Key>Model Category</SummaryList.Key>
                                <SummaryList.Value>{selectedPendingImplant.modelCategory}</SummaryList.Value>
                            </SummaryList.Row>
                            <SummaryList.Row>
                                <SummaryList.Key>MRI Conditional</SummaryList.Key>
                                <SummaryList.Value>{selectedPendingImplant.mrConditional ? 'Yes' : 'No'}</SummaryList.Value>
                            </SummaryList.Row>
                            <SummaryList.Row>
                                <SummaryList.Key>Organisation</SummaryList.Key>
                                <SummaryList.Value>{selectedPendingImplant.organisationName}</SummaryList.Value>
                            </SummaryList.Row>
                            <SummaryList.Row>
                                <SummaryList.Key>Created By</SummaryList.Key>
                                <SummaryList.Value>{selectedPendingImplant.createdByUserName}</SummaryList.Value>
                            </SummaryList.Row>
                            <SummaryList.Row>
                                <SummaryList.Key>Created At</SummaryList.Key>
                                <SummaryList.Value>{formatDate(selectedPendingImplant.createdAt)}</SummaryList.Value>
                            </SummaryList.Row>
                            {selectedPendingImplant.reviewStatus !== 'Pending' && (
                                <>
                                    <SummaryList.Row>
                                        <SummaryList.Key>Reviewed By</SummaryList.Key>
                                        <SummaryList.Value>{selectedPendingImplant.reviewedByUserName || '-'}</SummaryList.Value>
                                    </SummaryList.Row>
                                    <SummaryList.Row>
                                        <SummaryList.Key>Reviewed At</SummaryList.Key>
                                        <SummaryList.Value>{formatDate(selectedPendingImplant.reviewedAt)}</SummaryList.Value>
                                    </SummaryList.Row>
                                    {selectedPendingImplant.reviewStatus === 'Rejected' && (
                                        <SummaryList.Row>
                                            <SummaryList.Key>Rejection Reason</SummaryList.Key>
                                            <SummaryList.Value>{selectedPendingImplant.rejectionReason || '-'}</SummaryList.Value>
                                        </SummaryList.Row>
                                    )}
                                </>
                            )}
                        </SummaryList>
                        <Button onClick={() => setDetailsModalOpen(false)}>Close</Button>
                    </>
                )}
            </Modal>

            {/* Reject Modal */}
            <Modal show={rejectModalOpen} handleClose={() => { setRejectModalOpen(false); setRejectionReason(''); }}>
                {selectedPendingImplant && (
                    <>
                        <h2>Reject Pending Implant</h2>
                        <p>You are about to reject the implant: <strong>{selectedPendingImplant.modelName}</strong></p>
                        <p>Please provide a reason for rejection:</p>
                        <Textarea
                            label="Rejection Reason"
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            rows={4}
                        />
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                            <Button
                                onClick={handleRejectSubmit}
                                disabled={!rejectionReason.trim() || rejecting}
                            >
                                {rejecting ? 'Rejecting...' : 'Confirm Rejection'}
                            </Button>
                            <Button
                                secondary
                                onClick={() => { setRejectModalOpen(false); setRejectionReason(''); }}
                            >
                                Cancel
                            </Button>
                        </div>
                    </>
                )}
            </Modal>
        </Container>
    );
}

